import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    clearSession,
    getToken,
    hasValidSession,
    isLogoutStorageEvent,
    isPublicPath,
    millisecondsUntilExpiry,
} from "../auth/session";

// setTimeout overflows past ~24.8 days and fires immediately, so long waits are
// chunked. Tokens are issued with a 30 day life, which is over that limit.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// Sends a partner back to the login screen once their session is no longer good.
//
// This replaces a setInterval that re-read a cookie every second and was never
// cleared. Polling bought nothing: a token's expiry is known up front, so the
// only moments the answer can change are a navigation, the tab coming back to
// the foreground, or another tab signing out. We check exactly those, plus
// mount, and schedule a single timer for the instant the token actually lapses.
const useSessionGuard = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const enforce = useCallback(() => {
        if (isPublicPath(window.location.pathname)) {
            return;
        }
        if (hasValidSession()) {
            return;
        }
        clearSession();
        navigate("/", { replace: true });
    }, [navigate]);

    useEffect(() => {
        enforce();
    }, [enforce, location.pathname]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                enforce();
            }
        };

        const onStorage = (event) => {
            if (isLogoutStorageEvent(event)) {
                enforce();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("storage", onStorage);

        // A tab sitting untouched on the dashboard would otherwise keep showing a
        // signed-in shell until something happened to prompt a re-check.
        const remaining = millisecondsUntilExpiry(getToken());
        const expiryTimer =
            remaining === null
                ? null
                : setTimeout(enforce, Math.min(remaining, MAX_TIMEOUT_MS));

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("storage", onStorage);
            if (expiryTimer) {
                clearTimeout(expiryTimer);
            }
        };
    }, [enforce, location.pathname]);
};

export default useSessionGuard;
